import { useCallback, useEffect, useState } from 'react';
import { Schema } from 'yup';
import { useForm, FieldValues } from 'react-hook-form';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import { useModuleEndpoint } from './useModuleEndpoint';
import { ModuleConfig } from '../../types/modules';

interface ServerField extends FieldValues {
  server?: { type: String; message: String };
}
export interface UseModuleConfigFormProps<T extends FieldValues = FieldValues> {
  onSubmitSuccess?: () => void;
  onSubmitError?: (errors: any) => void;
  // Transform the config data received from the config endpoint into FormData T
  configAdapter: (data: ModuleConfig) => T;
  // Transform FormData T into payload for save endpoint
  submitAdapter?: (data: T) => any;
  // Yup validation for FormData
  validationSchema?: Schema<T>;
  subpath?: string;
}

// Get module config from the module endpoint
// configAdapter: Transforms the config data into form data
// submitAdapter: Transforms the form data into request data
// validate: Validates form values, returns errors on failure
export function useModuleConfigForm<T extends FieldValues = FieldValues>(
  moduleId: number,
  {
    onSubmitSuccess,
    onSubmitError,
    configAdapter,
    submitAdapter = x => x,
    validationSchema,
    subpath = '',
  }: UseModuleConfigFormProps<T>
) {
  const [renderVersion, setRenderVersion] = useState<number>();
  const {
    response: configResponse = {},
    getEndpoint: configFetch,
    reset: resetConfig,
    isFetching,
    error: configError,
  } = useModuleEndpoint(moduleId, `${subpath}/config`);

  const {
    postEndpoint: saveModuleConfig,
    isFetching: isSaving,
    hasJustFinishedFetching: hasJustFinishedSaving,
    error: saveError,
  } = useModuleEndpoint(moduleId, `${subpath}/save`);

  const { config } = configResponse;
  const isInitialized = !!config;

  const formContext = useForm<T | ServerField>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver:
      validationSchema &&
      yupResolver(validationSchema, {
        abortEarly: false,
      }),
    defaultValues: configAdapter(configResponse) as any,
  });
  const { reset, handleSubmit, errors, setError } = formContext;

  const hasError = saveError || Object.keys(errors).length > 0;
  useEffect(() => {
    if (configError || saveError) {
      setError('server', { type: 'server', message: configError || saveError });
    }
  }, [setError, configError, saveError]);

  // Fetch config on initial mount
  useEffect(() => {
    // moduleError check here is to prevent infinite loop and self DDoS
    // for now, users will have to reload the page to clear the error
    if (!isFetching && !config && !configError) {
      configFetch();
    }
  }, [config, isFetching, configFetch, configError]);

  // Close upon successful save
  useEffect(() => {
    if (hasJustFinishedSaving && !hasError) {
      onSubmitSuccess?.();
      // this clears the config endpoint data from the store, forcing a refetch
      resetConfig();
    }
  }, [hasJustFinishedSaving, hasError, onSubmitSuccess, resetConfig]);

  // If module config data has changed, transform the data through
  // configAdapter and store it in the local reference
  useEffect(() => {
    if (
      configResponse?.config &&
      configResponse.config.version !== renderVersion
    ) {
      // Transform the module config and options through a configAdapter into
      reset(configAdapter(configResponse) as any);
      // Trigger re-render if the version changed
      setRenderVersion(configResponse.config.version);
    }
  }, [reset, configResponse, renderVersion, configAdapter]);

  // Start saving config
  const requestSubmit = useCallback(() => {
    if (isSaving) return;
    handleSubmit(
      data => {
        saveModuleConfig(submitAdapter(data as any));
      },
      submitErrors => {
        onSubmitError && onSubmitError(submitErrors);
      }
    )();
  }, [isSaving, handleSubmit, saveModuleConfig, submitAdapter, onSubmitError]);

  return {
    formContext,
    requestSubmit,
    configResponse,
    isInitialized,
    errors,
  };
}
